import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Shield, AlertTriangle, CheckCircle2 } from "lucide-react";
import { supabase } from "@core/lib/supabase";
import { Button } from "@core/components/ui/button";
import { Input } from "@core/components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@core/components/ui/card";

type ViewState =
  | "checking"
  | "ready"
  | "submitting"
  | "success"
  | "invalid"
  | "error";

/**
 * Page atterrissage du lien reçu par email "Réinitialiser votre mot de passe".
 * La config PKCE + detectSessionInUrl de lib/supabase.ts consomme
 * automatiquement le token dans le hash URL et établit la session recovery.
 */
export default function ResetPasswordPage() {
  const { t } = useTranslation("forms");
  const navigate = useNavigate();
  const [viewState, setViewState] = useState<ViewState>("checking");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let recovered = false;

    // Filet de sécurité : événement PASSWORD_RECOVERY
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        recovered = true;
        setViewState("ready");
      }
    });

    // Vérification initiale : si la session a déjà été consommée
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        recovered = true;
        setViewState("ready");
        return;
      }
      // Laisser un court délai pour que detectSessionInUrl consomme le hash
      setTimeout(() => {
        if (!recovered) {
          setViewState("invalid");
        }
      }, 1500);
    });

    return () => subscription.unsubscribe();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMessage(null);

    if (newPassword.length < 8) {
      setErrorMessage(t("auth.reset.passwordTooShort"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage(t("auth.reset.passwordsMismatch"));
      return;
    }

    setViewState("submitting");
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) throw error;
      setViewState("success");
      setTimeout(() => {
        navigate("/account/security", { replace: true });
      }, 2500);
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : t("auth.reset.updateError");
      setErrorMessage(msg);
      setViewState("ready");
    }
  }

  return (
    <div
      className="min-h-screen-safe bg-gray-950 flex items-center justify-center p-4"
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md">
        <Card className="border-gray-800 bg-gray-900/80">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Lock className="w-5 h-5 text-blue-400" />
              {t("auth.reset.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {viewState === "checking" && (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3" />
                <p className="text-sm text-gray-400">
                  {t("auth.reset.checking")}
                </p>
              </div>
            )}

            {viewState === "invalid" && (
              <div
                className="flex items-start gap-3 p-3 bg-red-900/20 border border-red-700/40 rounded-lg"
                data-testid="reset-password-invalid"
              >
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <div className="space-y-2">
                  <p className="text-sm text-red-300">
                    {t("auth.reset.invalid")}
                  </p>
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="border-red-700/60 text-red-300 hover:bg-red-950/40"
                  >
                    <Link to="/account/security">
                      {t("auth.reset.backToSecurity")}
                    </Link>
                  </Button>
                </div>
              </div>
            )}

            {viewState === "success" && (
              <div
                className="flex items-start gap-3 p-3 bg-emerald-900/20 border border-emerald-700/40 rounded-lg"
                data-testid="reset-password-success"
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <p className="text-sm text-emerald-300">
                  {t("auth.reset.success")}
                </p>
              </div>
            )}

            {(viewState === "ready" || viewState === "submitting") && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-sm text-gray-400">
                  {t("auth.reset.instructions")}
                </p>

                <div className="space-y-1">
                  <label
                    htmlFor="new-password"
                    className="text-sm text-gray-300"
                  >
                    {t("auth.reset.newPasswordLabel")}
                  </label>
                  <Input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={viewState === "submitting"}
                    required
                    minLength={8}
                    data-testid="new-password-input"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                <div className="space-y-1">
                  <label
                    htmlFor="confirm-password"
                    className="text-sm text-gray-300"
                  >
                    {t("auth.reset.confirmLabel")}
                  </label>
                  <Input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={viewState === "submitting"}
                    required
                    minLength={8}
                    data-testid="confirm-password-input"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                {errorMessage && (
                  <div
                    className="flex items-start gap-2 p-2 bg-red-900/20 border border-red-700/40 rounded"
                    data-testid="reset-password-error"
                  >
                    <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                    <p className="text-xs text-red-300">{errorMessage}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={viewState === "submitting"}
                  data-testid="submit-new-password"
                  className="w-full gap-2"
                >
                  <Shield className="w-4 h-4" />
                  {viewState === "submitting"
                    ? t("auth.reset.submitting")
                    : t("auth.reset.submit")}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
